import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { MarinaSetting } from '../../models/types';
import { 
  getMarinaSettings, 
  updateMarinaSettings
} from '../../firebase/services/marinaSettingsService';
import MarinaAdminLayout from '../../layouts/MarinaAdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { app, functions } from '../../firebase/config';
import { getRoleById } from '../../firebase/services/roleService';
import { Role } from '../../types/rbac';

const PaymentSettingsPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [settings, setSettings] = useState<MarinaSetting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<'loading' | 'success' | 'error' | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [roleName, setRoleName] = React.useState<string | null>(null);
  const [roleLoading, setRoleLoading] = React.useState<boolean>(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  React.useEffect(() => {
    const fetchRole = async () => {
      if (userProfile && userProfile.roleId) {
        setRoleLoading(true);
        const roleDoc = await getRoleById(userProfile.roleId);
        setRoleName(roleDoc?.name || null);
        setRoleLoading(false);
      } else {
        setRoleName(null);
        setRoleLoading(false);
      }
    };
    fetchRole();
  }, [userProfile]);

  const fetchSettings = async () => {
    try {
      const data = await getMarinaSettings();
      // Initialize activePaymentMethods if it doesn't exist
      if (!data.activePaymentMethods) {
        data.activePaymentMethods = {
          square: false
        };
      }
      // Initialize squareEnvironment if it doesn't exist
      if (!data.squareEnvironment) {
        data.squareEnvironment = 'sandbox';
      }
      setSettings(data);
    } catch (err) {
      setError('Failed to load payment settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof MarinaSetting, value: string) => {
    if (!settings) return;
    
    setSettings(prev => ({
      ...prev!,
      [field]: value
    }));
  };

  const handleInputBlur = async (field: keyof MarinaSetting) => {
    if (!settings) return;
    
    // Skip if the field value is undefined
    if (settings[field] === undefined) return;
    
    try {
      await updateMarinaSettings({ [field]: settings[field] });
      setSuccess(`${field} updated successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to update ${field}`);
      console.error(err);
    }
  };

  const testSquareConnection = async () => {
    if (!settings?.squareApplicationId || !settings?.squareLocationId || !settings?.squareAccessToken) {
      setTestError('Please enter all Square credentials first');
      return;
    }

    try {
      setTestStatus('loading');
      
      // Use our Cloud Function to validate Square credentials (testSquareConnectionHttp properly sets CORS headers)
      const functionsUrl = 'https://us-central1-fllboats.cloudfunctions.net/testSquareConnectionHttp';
      
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          applicationId: settings.squareApplicationId,
          locationId: settings.squareLocationId,
          accessToken: settings.squareAccessToken,
          environment: settings.squareEnvironment || 'sandbox'
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setTestStatus('success');
        setTestError(null);
        
        // Enable Square payments if connection successful
        await updateMarinaSettings({
          activePaymentMethods: {
            ...settings.activePaymentMethods,
            square: true
          }
        });
        
        setSettings(prev => ({
          ...prev!,
          activePaymentMethods: {
            ...prev!.activePaymentMethods,
            square: true
          }
        }));
        
        // Show success message
        setSuccess(`Connected to Square: ${result.locationName || 'Location'}`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setTestStatus('error');
        setTestError(result.error || 'Failed to connect to Square. Check your credentials.');
      }
      
    } catch (err) {
      console.error('Error testing Square connection:', err);
      setTestStatus('error');
      setTestError('Failed to test connection. Check your network connection and try again.');
    }
  };

  if (roleLoading) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3 }}><CircularProgress /></Box>
      </MarinaAdminLayout>
    );
  }

  if (!userProfile || roleName !== 'admin') {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3 }}>
          <Typography color="error">You don't have permission to access this page</Typography>
        </Box>
      </MarinaAdminLayout>
    );
  }

  if (loading) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3 }}></Box>
      </MarinaAdminLayout>
    );
  }

  if (!settings) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3 }}>
          <Typography color="error">Failed to load payment settings</Typography>
        </Box>
      </MarinaAdminLayout>
    );
  }

  return (
    <MarinaAdminLayout>
      <Box sx={{ p: 3 }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        {success && (
          <Typography color="success" sx={{ mb: 2 }}>{success}</Typography>
        )}
        
        <Typography variant="h4" gutterBottom>
          Square Payment Settings
        </Typography>

        <Grid container spacing={3}>
          {/* Square Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Square API Credentials
                </Typography>
                
                {testStatus === 'success' && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Connection to Square API successful! Square payments are now enabled.
                  </Alert>
                )}
                
                {testStatus === 'error' && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {testError || 'Connection to Square API failed. Please check your credentials.'}
                  </Alert>
                )}
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.activePaymentMethods.square}
                          onChange={(e) => {
                            setSettings(prev => ({
                              ...prev!,
                              activePaymentMethods: {
                                ...prev!.activePaymentMethods,
                                square: e.target.checked
                              }
                            }));
                          }}
                        />
                      }
                      label="Enable Square Payments"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Square Application ID"
                      value={settings.squareApplicationId || ''}
                      onChange={(e) => handleInputChange('squareApplicationId', e.target.value)}
                      onBlur={() => handleInputBlur('squareApplicationId')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Square Location ID"
                      value={settings.squareLocationId || ''}
                      onChange={(e) => handleInputChange('squareLocationId', e.target.value)}
                      onBlur={() => handleInputBlur('squareLocationId')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Square Access Token"
                      type="password"
                      value={settings.squareAccessToken || ''}
                      onChange={(e) => handleInputChange('squareAccessToken', e.target.value)}
                      onBlur={() => handleInputBlur('squareAccessToken')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Square Environment"
                      value={settings.squareEnvironment || 'sandbox'}
                      onChange={(e) => {
                        const value = e.target.value as 'sandbox' | 'production';
                        setSettings(prev => ({
                          ...prev!,
                          squareEnvironment: value
                        }));
                        // Save immediately
                        updateMarinaSettings({ squareEnvironment: value })
                          .then(() => {
                            setSuccess('Square environment updated successfully');
                            setTimeout(() => setSuccess(null), 3000);
                          })
                          .catch(err => {
                            setError('Failed to update Square environment');
                            console.error(err);
                          });
                      }}
                    >
                      <MenuItem value="sandbox">Sandbox (Testing)</MenuItem>
                      <MenuItem value="production">Production (Live)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={testSquareConnection}
                      disabled={testStatus === 'loading'}
                    >
                      {testStatus === 'loading' ? 'Testing...' : 'Test Square Connection'}
                    </Button>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Troubleshooting
                  </Typography>
                  <Typography variant="body2">
                    1. Ensure your Square credentials are correct and from the correct environment (Sandbox or Production)
                  </Typography>
                  <Typography variant="body2">
                    2. Make sure the Cloud Function "processSquarePaymentCallable" is deployed
                  </Typography>
                  <Typography variant="body2">
                    3. Check that your Square account has the appropriate permissions enabled
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </MarinaAdminLayout>
  );
};

export default PaymentSettingsPage; 